import { INLINE_LINK } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { SubprocessorTable } from '../../features/trust/components/SubprocessorTable';
import { subprocessorDocument } from '../../features/trust/content/subprocessors';

import type { ReactElement } from 'react';

/**
 * Trust Center — subprocessors page (`/trust/subprocessors`).
 * Phase 6 Pass 3 iter 243.
 *
 * Per `pass-3-trust-center.md` § Pages → `/trust/subprocessors`:
 * "Live list + RSS feed + 30-day-change-notice policy."
 *
 * **Iter-243** shipped the live list + 30-day policy + last-updated
 * marker. **Iter-244** wires the RSS feed at
 * `/trust/subprocessors.rss` — generated at build time by
 * `scripts/generate-rss.ts` from the same JSON SOT.
 *
 * **Marketing voice (Marketer hat, locked)**: leads with the
 * 30-day-change-notice commitment + the last-updated date so a
 * procurement reader can confirm freshness without an email. The
 * vendor list itself lives in the JSON; this page is the table +
 * the policy framing.
 *
 * **i18n**: page chrome + the table's descriptive prose and column
 * headers resolve through `useT()` under the `subprocessors.*`
 * namespace. Vendor names, primary locations, transfer mechanisms,
 * and data-category values stay verbatim (they are the legal record
 * from the customer DPA). This is a binding legal document, so the
 * `LegalTranslationNotice` renders directly under the `<h1>` (it
 * self-hides on the English locale).
 *
 * **Three-layer contract preserved**: pure presentational
 * composition. Data source is the JSON loader. No state.
 */
export function Subprocessors(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('subprocessors.meta.title'),
    description: t('subprocessors.meta.description'),
    path: '/trust/subprocessors',
  });
  return (
    <LegalPage
      breadcrumb={t('subprocessors.breadcrumb')}
      title={t('subprocessors.title')}
      intro={t('subprocessors.intro', { days: subprocessorDocument.policyDays })}
      width="default"
    >
      {/* This page's revision stamp is its OWN, not the shared
          `LegalLastUpdated`: the date comes from `subprocessorDocument`, which
          is the same source the RSS feed publishes, and the sentence continues
          into the feed + escalation links. Splitting the date away from them
          would leave three fragments where the page has one statement. */}
      <p className="mb-8 text-sm text-[color:var(--bd-muted)]">
        {t('subprocessors.lastUpdated')}{' '}
        <time dateTime={subprocessorDocument.lastUpdated}>{subprocessorDocument.lastUpdated}</time>.{' '}
        {t('subprocessors.subscribe.before')}{' '}
        <a className={INLINE_LINK} href="/trust/subprocessors.rss" type="application/rss+xml">
          {t('subprocessors.subscribe.rss')}
        </a>{' '}
        {t('subprocessors.subscribe.middle')}{' '}
        <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
          admin@syncanix.com
        </a>
        .
      </p>

      <SubprocessorTable />
    </LegalPage>
  );
}
